<template>
  <div></div>
</template>
<script>
import { mapGetters } from 'vuex';
import { throttle } from 'lodash-es';
import { getGroupChatDomain } from '@/constants/customInfo';

export default {
  name: 'GroupChatWindow',
  data() {
    return {
      isChatWindowOpen: false,
      newWindow: null,
      x: 10,
      y: 10,
      id: '1',
      backgroundColor: 'blue',
      debouncedShowMessage: null,
      showNewMessageCount: true,
      childWindowStatus: 'VISIBLE',
      prevCount: 0
    };
  },
  computed: {
    ...mapGetters(['getWorkPlaceId', 'getAccountId', 'getLoginId', 'isLoggedIn']),
    IFRAME_URL() {
      return `${this.IFRAME_DOMAIN}?workplaceId=${this.getWorkPlaceId}&accountId=${this.getAccountId}&loginId=${this.getLoginId}`;
    },
    IFRAME_DOMAIN() {
      return getGroupChatDomain();
    }
  },
  created() {
    // ** App.vue 에서 보내는 이벤트
    // ** 앱 전체의 사용자 이벤트를 감지해서 이벤트 없을 경우 최종적으로 10분 타이머 동작해서
    // postMessage 로 자식창에 잠금모드 활성화 이벤트를 요청한다
    this.$eventBus.$on('change-lock-status', (value) => {
      if (value) {
        if (this.newWindow) {
          this.newWindow.postMessage(
            {
              type: 'CHANGE_LOCK_STATUS',
              payload: {
                status: 'ON'
              }
            },
            this.IFRAME_DOMAIN
          );
        }
      }
    });

    // ** 헤더에서 보내는 이벤트
    // ** 이미 열려있는 상태에서는 보내지않음
    this.$eventBus.$on('change-chat-layer-on', (value) => {
      if (value) {
        if (!this.newWindow || (this.newWindow && this.newWindow.closed === true)) {
          this.newWindow = window.open(
            `${this.IFRAME_URL}`,
            'groupChatPopup',
            'width=390,height=715,toolbar=no,menubar=no,scrollbars=no,resizable=no'
          );
          this.isChatWindowOpen = value;
        }
      } else {
        if (this.newWindow && !this.newWindow.closed) {
          this.newWindow.close();
        }
      }
    });

    // ** 로그아웃 시에만 실행하는 이벤트
    this.$eventBus.$on('close-group-chat', () => {
      if (this.newWindow && this.newWindow.closed === false) {
        this.isChatWindowOpen = false;
        this.newWindow.close();
        this.$store.commit('setCgChatStatus', false);
        this.$store.commit('setCgChatNewMessageBadge', false);
      }
    });

    this.debouncedShowMessage = throttle(
      (payload) => {
        if (!this.showNewMessageCount) {
          return;
        }

        const count = payload.count;
        if (count === 0) {
          return;
        }
        const isOver = count >= 99;
        this.$toasted.info(`<b>${isOver ? 99 : count}${isOver ? '+' : ''}개</b>의 새로운 메시지가 도착했습니다.`, {
          duration: 2500
        });
      },
      3000,
      {
        trailing: true
      }
    );

    window.addEventListener('resize', this.onResize);
    window.addEventListener('message', this.messageHandler);
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('message', this.messageHandler);
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    this.$eventBus.$off('change-chat-layer-on');
    this.$eventBus.$off('close-group-chat');
  },
  methods: {
    beforeUnloadHandler() {
      // ** 새로고침 시에 자식으로 마지막 요청 보내놓음
      if (this.newWindow) {
        this.newWindow.postMessage(
          {
            type: 'BEFOREUNLOAD_PARENT_WINDOW',
            payload: {
              timestamp: Date.now()
            }
          },
          this.IFRAME_DOMAIN
        );
      }
    },
    messageHandler(event) {
      // origin 검사 (보안상 반드시 필요)
      if (event.origin !== this.IFRAME_DOMAIN) return;

      const { type, payload } = event.data;

      if (type === 'CLOSE_NEW_WINDOW') {
        setTimeout(() => {
          if (!this.newWindow || (this.newWindow && this.newWindow.closed === true)) {
            // 그룹챗 창을 끈 경우
            this.$eventBus.$emit('change-chat-icon-status', false);
            this.isChatWindowOpen = false;
            this.$store.commit('setCgChatStatus', false);
          } else {
            // 그룹챗 창을 새로고침한 경우
            this.$eventBus.$emit('change-chat-icon-status', true);
            this.isChatWindowOpen = true;
            this.$store.commit('setCgChatStatus', true);
          }
        }, 300);
      } else if (type === 'DIRECT_CLOSE_NEW_WINDOW') {
        if (this.newWindow) {
          this.isChatWindowOpen = false;
          this.newWindow.close();
          this.$eventBus.$emit('change-chat-icon-status', false);
          this.$store.commit('setCgChatStatus', false);
        } else {
          this.isChatWindowOpen = false;
          this.$eventBus.$emit('change-chat-icon-status', false);
          this.$store.commit('setCgChatStatus', false);
        }
      } else if (type === 'THEME') {
        this.backgroundColor = payload.theme;
      } else if (type === 'NEW_MESSAGE_ALARM') {
        this.$eventBus.$emit('change-chat-new-message', payload.show);
      } else if (type === 'NEW_MESSAGE_ALARM_COUNT') {
        // 새 창 비활성화로 postMessage로 강제 갱신해줬던 경우
        // 알람 카운트 싱크 맞추기 위해 새 창 활성화 시 보내는 알림은
        // 이미 갱신된 만큼은 무시

        // 줄어든 숫자에 대해서도 전달은 되지만
        // 다음 카운팅 싱크 맞추기위해서 갱신만해주고 실제로 토스트 알림 표시는 안해줌
        if (this.prevCount >= payload.count) {
          this.prevCount = payload.count;
          return;
        }

        // 최종적으로 받은 카운트는 항시 저장
        // 새 창 비활성화 시 사용 필요
        this.prevCount = payload.count;

        if (payload.init) {
          return;
        }

        this.debouncedShowMessage(payload);
      } else if (type === 'USE_NEW_MESSAGE_ALARM') {
        this.showNewMessageCount = payload.use;
      } else if (type === 'NEW_CHILD_WINDOW') {
        // ** 자식창의 연결이 부모창의 새로고침으로 인해 유실되었을 때 해결책
        this.newWindow = event.source;
      } else if (type === 'CHILD_WINDOW_VISIBLE') {
        // 창이 다시 활성화
        this.childWindowStatus = 'VISIBLE';
      } else if (type === 'CHILD_WINDOW_HIDDEN') {
        // 창이 다시 비활성화
        this.childWindowStatus = 'HIDDEN';
      } else if (type === 'HIDDEN_UPDATE') {
        this.debouncedShowMessage({ count: ++this.prevCount });
      }
    }
  }
};
</script>
<style lang="scss">
.chat-layer {
  position: fixed;
  border-radius: 15px;

  min-width: 390px;
  min-height: 715px;

  background-color: #5578f0;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);

  &.white {
    background-color: #fff;
  }

  .chat-layer-move-background {
    height: 20px;
    opacity: 0;
  }

  .chat-layer-move-icon {
    cursor: grab;
    height: 15px;
    background-color: #5578f0;
    border-radius: 15px 15px 0 0;
    text-align: center;

    i {
      font-size: 20px;
      color: #fff;
    }

    &.white {
      background-color: #fff;
      i {
        color: #bebebe;
      }
    }
  }

  .chat-layer-content {
    position: relative;
    height: calc(100% - 15px);
    border-radius: 15px;

    .chat-layer-wrapper {
      cursor: grab;
      // overflow: hidden;
      height: 100%;
    }

    .chat-layer-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 22px;
      border-radius: 15px;
      z-index: 2;
    }
  }

  .full-iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    border: none;
  }

  .handle {
    width: 10px;
    height: 10px;
    position: absolute;
    box-sizing: border-box;
  }
  .handle-bl {
    bottom: -15px;
    left: -15px;
    cursor: sw-resize;
  }
  .handle-br {
    bottom: -15px;
    right: -15px;
    cursor: se-resize;
  }

  .handle-br {
    display: flex !important;
    .Triangle1 {
      width: 0;
      height: 0;
      border-top: 8px solid $gray-7;
      border-right: 8px solid transparent;
      float: left;
    }
    .Triangle2 {
      width: 0;
      height: 0;
      border-bottom: 8px solid $gray-7;
      border-left: 8px solid transparent;
      float: left;
      margin-left: -7px;
      margin-top: 1px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/GroupChatWindow.vue